import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { expensesAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAccountant, isAdmin } from "../lib/roles";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Receipt, Eye, ArrowsClockwise, Warning, Copy, ArrowSquareOut, Clock, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import ReceiptPreview, { ReceiptBadge } from "../components/ReceiptPreview";
import { formatDate } from "../lib/formatDate";
import { formatCurrency } from "../lib/formatCurrency";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import Comments from "../components/Comments";
import { QuickFilters } from "../components/QuickFilters";
import { CodingCell } from "../components/CodingCell";
import { AutopilotBadge } from "../components/AutopilotBadge";
import { MakeAutopilotRuleButton } from "../components/MakeAutopilotRuleButton";
import { Input } from "../components/ui/input";

const PUSH_POLL_DELAY_MS = 2500;
// How many recently-approved expenses to keep loaded in the UI. The list is
// client-side filterable (by the search field + quick filters), so this is
// the upper bound of what users can scan/search through without a full page
// reload. Tuned high enough to cover ~2 months of typical company volume.
const APPROVED_LIST_LIMIT = 200;

// Compute which targets a given expense routes to (mirrors backend `_split_target`).
function _expenseTargets(exp) {
  const splits = exp.splits?.length > 0 ? exp.splits : [{
    type: exp.expense_type,
    budget_item_id: exp.budget_item_id,
  }];
  const set = new Set();
  splits.forEach((s) => {
    if (s.type === "job" && (s.budget_item_id || "").trim()) set.add("jt");
    else set.add("qbo");
  });
  return set;
}

function QboStatusBadge({ exp }) {
  const targets = _expenseTargets(exp);
  if (!targets.has("qbo")) return null;  // expense doesn't route to QBO at all
  if (exp.qbo_purchase_id) {
    return (
      <Badge data-testid={`qbo-badge-pushed-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 gap-0.5">
        <CheckCircle size={9} weight="bold" /> QBO #{exp.qbo_purchase_id}
      </Badge>
    );
  }
  if (exp.qbo_push_error) {
    return (
      <Badge data-testid={`qbo-badge-failed-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-amber-50 text-amber-700 border-amber-200 gap-0.5">
        <Warning size={9} weight="bold" /> QBO failed
      </Badge>
    );
  }
  // Held — approved while the card swipe was pending. QBO push fires
  // automatically the moment the swipe posts (provided amount matches).
  if (exp.awaiting_post) {
    return (
      <Badge data-testid={`qbo-badge-awaiting-post-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-amber-50 text-amber-800 border-amber-300 gap-0.5"
        title="Approved — QuickBooks push is held until the linked card swipe posts in Mercury.">
        <Clock size={9} weight="bold" /> QBO held
      </Badge>
    );
  }
  return (
    <Badge data-testid={`qbo-badge-pending-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-zinc-100 text-zinc-500 border-zinc-200">
      QBO pushing…
    </Badge>
  );
}

function JtStatusBadge({ exp }) {
  const targets = _expenseTargets(exp);
  if (!targets.has("jt")) return null;  // expense doesn't route to JobTread
  if (exp.jt_document_id) {
    return (
      <Badge data-testid={`jt-badge-pushed-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 gap-0.5">
        <CheckCircle size={9} weight="bold" /> JT {exp.jt_document_id.slice(0, 6)}
      </Badge>
    );
  }
  if (exp.jt_push_error) {
    return (
      <Badge data-testid={`jt-badge-failed-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-amber-50 text-amber-700 border-amber-200 gap-0.5"
        title={exp.jt_push_error}>
        <Warning size={9} weight="bold" /> JT failed
      </Badge>
    );
  }
  if (exp.awaiting_post) {
    return (
      <Badge data-testid={`jt-badge-awaiting-post-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-amber-50 text-amber-800 border-amber-300 gap-0.5"
        title="Approved — JobTread push is held until the linked card swipe posts in Mercury.">
        <Clock size={9} weight="bold" /> JT held
      </Badge>
    );
  }
  return (
    <Badge data-testid={`jt-badge-pending-${exp.id}`} className="text-[9px] uppercase rounded-sm bg-zinc-100 text-zinc-500 border-zinc-200">
      JT pushing…
    </Badge>
  );
}

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = isAccountant(user);
  const [expenses, setExpenses] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pushing, setPushing] = useState({}); // {expenseId: boolean}
  const [errorDialog, setErrorDialog] = useState(null);
  const [repushConfirm, setRepushConfirm] = useState(null);
  const [unlinkJtConfirm, setUnlinkJtConfirm] = useState(null);
  const [unlinkingJt, setUnlinkingJt] = useState({});
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const baseParams = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) baseParams[k] = v; });
      const [pendingRes, approvedRes] = await Promise.all([
        expensesAPI.list({ ...baseParams, status: "submitted" }),
        expensesAPI.list({ ...baseParams, status: "approved", limit: APPROVED_LIST_LIMIT }),
      ]);
      setExpenses(pendingRes.data.expenses);
      setApproved(approvedRes.data.expenses);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh: poll every 60s so newly submitted expenses surface without a reload.
  useEffect(() => {
    const id = setInterval(() => { fetchAll(); }, 60000);
    return () => clearInterval(id);
  }, [fetchAll]);

  // Re-sort the approved list by date desc, then created_at desc — same key
  // the backend uses — so post-mutation refreshes (e.g. after a re-push)
  // keep rows in their natural chronological order instead of bumping the
  // refreshed one to the top.
  const sortApproved = (rows) => [...rows].sort((a, b) => {
    const da = (a.date || "");
    const db = (b.date || "");
    if (da !== db) return db.localeCompare(da);
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  const refreshSingleApproved = async (id) => {
    try {
      const { data } = await expensesAPI.get(id);
      setApproved((prev) => {
        const without = prev.filter((e) => e.id !== id);
        return sortApproved([data, ...without]).slice(0, APPROVED_LIST_LIMIT);
      });
    } catch (e) {
      console.warn(`Failed to refresh expense ${id}:`, e);
    }
  };

  // Client-side search filter — matches vendor, submitter, job, GL, card last 4, EXP id.
  // Applied to both pending and approved lists so the same search bar drives
  // both sections at once (mirrors the UX on Expenses + Card Transactions).
  const matchesSearch = (exp) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const hay = [
      exp.vendor,
      exp.submitted_by_name,
      exp.job_name,
      exp.gl_account_name,
      exp.card_last_four,
      exp.expense_number ? `EXP-${exp.expense_number}` : "",
      exp.description,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const visiblePending = expenses.filter(matchesSearch);
  const visibleApproved = approved.filter(matchesSearch);

  const handleApprove = async (id) => {
    try {
      const exp = expenses.find((e) => e.id === id);
      const { data } = await expensesAPI.approve(id);
      // Three flavors of success message:
      //   1. Training/simulated expense — no real push happens.
      //   2. Real approval but the linked card swipe hasn't posted yet — the
      //      backend held the QBO/JT push; it'll auto-fire on the post webhook
      //      provided the final amount matches.
      //   3. Standard real approval — QBO/JT push fires immediately.
      if (exp?.is_simulated) {
        toast.success("Training expense approved (skipped QBO/JT — no real data posted)");
      } else if (data?.awaiting_post) {
        toast.success("Approved — push to QuickBooks/JobTread held until the card swipe posts", {
          description: "If the posted amount matches, the push fires automatically. If it changes, it'll bounce back here for re-approval.",
          duration: 7000,
        });
      } else {
        toast.success("Expense approved — pushing to QuickBooks…");
      }
      setSelected(null);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      // Optimistically add a stub to the approved list
      const stub = expenses.find((e) => e.id === id);
      if (stub) {
        setApproved((prev) => [{ ...stub, status: "approved", awaiting_post: !!data?.awaiting_post }, ...prev].slice(0, 20));
      }
      // Poll the expense after 2.5s to surface QBO push result (or the awaiting_post badge)
      setTimeout(() => refreshSingleApproved(id), PUSH_POLL_DELAY_MS);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (id) => {
    // Prompt for a short reason so the submitter sees it inline on their
    // edit form (red banner) and inside the notification. Empty / Cancel
    // is allowed — produces a generic rejection.
    const reason = window.prompt(
      "Why is this expense being rejected?\n\nThe submitter will see this in their notification and on the edit form. Leave blank if no specific reason.",
      ""
    );
    if (reason === null) return; // user clicked Cancel
    try {
      await expensesAPI.reject(id, (reason || "").trim());
      toast.success("Expense rejected — submitter notified");
      setSelected(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const handleRepush = async (id) => {
    setPushing((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await expensesAPI.push(id, true);
      const parts = [];
      if (data.qbo_purchase_id) parts.push(`QBO #${data.qbo_purchase_id}`);
      if (data.jt_document_id) parts.push(`JT ${data.jt_document_id.slice(0, 6)}`);
      const errParts = [];
      if (data.qbo_error) errParts.push(`QBO: ${data.qbo_error}`);
      if (data.jt_error) errParts.push(`JobTread: ${data.jt_error}`);
      if (data.pushed && parts.length > 0) {
        toast.success(`Pushed — ${parts.join(" · ")}`);
      } else if (errParts.length > 0) {
        toast.error(`Push failed: ${errParts.join(" | ")}`, {
          description: data.note || undefined,
          duration: data.note ? 10000 : 5000,
        });
      } else {
        toast.success("Pushed");
      }
      refreshSingleApproved(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-push failed");
    } finally {
      setPushing((p) => ({ ...p, [id]: false }));
      setRepushConfirm(null);
    }
  };

  const confirmRepush = (exp) => {
    // Failed pushes (no prior id on either system) can re-push without confirmation — nothing to void.
    if (!exp.qbo_purchase_id && !exp.jt_document_id) {
      handleRepush(exp.id);
      return;
    }
    setRepushConfirm(exp);
  };

  // Admin-only escape hatch: clear our stored JT document references on this
  // expense without calling JT (used when JT has locked the doc in `approved`
  // status and refuses to demote/delete via API). User must have already
  // manually deleted the doc(s) in JT UI first.
  const handleForceUnlinkJt = async (id, reason) => {
    setUnlinkingJt((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await expensesAPI.forceUnlinkJt(id, reason || "");
      toast.success(`JT IDs cleared: ${data.cleared_ids.join(", ")}`);
      setUnlinkJtConfirm(null);
      refreshSingleApproved(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force-unlink failed");
    } finally {
      setUnlinkingJt((p) => ({ ...p, [id]: false }));
    }
  };

  const copyErr = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success("Error copied")).catch(() => {});
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="approvals-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "DM Sans, sans-serif" }}>
          Approval Queue
        </h1>
        <p className="text-sm text-zinc-500 mt-0.5">{expenses.length} expenses pending review</p>
      </div>

      <div className="mb-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            data-testid="approvals-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor, submitter, job, GL, last 4, EXP-…"
            className="pl-8 rounded-sm h-8 text-sm"
          />
        </div>
        <div className="flex-shrink-0">
          <QuickFilters value={filters} onChange={setFilters} show={["submitter", "vendor", "job", "gl", "card"]} />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center animate-pulse text-zinc-400">Loading...</div>
        ) : visiblePending.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
              <div className="col-span-1">Date</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Vendor</div>
              <div className="col-span-2">Submitted By</div>
              <div className="col-span-2">Coding</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {visiblePending.map((exp) => {
              const isPending = (exp.mercury_txn_status || "").toLowerCase() === "pending";
              // When the linked swipe POSTED at a different amount than what
              // the approver originally signed off on, the backend bounces
              // the expense back to the queue with this flag. Render a loud
              // red banner so the approver sees the discrepancy at a glance.
              const amountChanged = !!exp.amount_changed_at_post;
              return (
              <div key={exp.id}
                className={`px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center transition-colors ${
                  amountChanged
                    ? "bg-red-50/60 hover:bg-red-50 border-l-2 border-red-400"
                    : isPending
                      ? "bg-amber-50/40 hover:bg-amber-50/70 border-l-2 border-amber-300"
                      : "hover:bg-zinc-50"
                }`}
                data-testid={`approval-row-${exp.id}`}
                title={amountChanged
                  ? `Posted amount changed — was $${(exp.prior_approved_amount ?? 0).toFixed(2)}, posted $${(exp.post_amount ?? exp.amount ?? 0).toFixed(2)}. Re-approve to release to QuickBooks.`
                  : isPending ? "Swipe is still pending in Mercury — you can approve now; the push to QuickBooks/JobTread will fire automatically when it posts (provided the amount doesn't change)." : ""}
              >
                <div className="col-span-1 text-sm text-zinc-700" data-testid={`approval-dates-${exp.id}`}>
                  <p className="font-medium">{formatDate(exp.transaction_date || exp.date)}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Posted: {exp.post_date
                      ? <span className="text-zinc-700">{formatDate(exp.post_date)}</span>
                      : !exp.mercury_transaction_id
                        ? <span className="inline-flex items-center px-1 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px] uppercase tracking-wider font-bold" title="Manually entered — no bank transaction" data-testid={`approval-manual-entry-${exp.id}`}>Manual Entry</span>
                        : <span className="text-zinc-300">—</span>}
                  </p>
                </div>
                <div className="col-span-1 mt-1 sm:mt-0">
                  {amountChanged ? (
                    <Badge data-testid={`amount-changed-badge-${exp.id}`}
                      className="text-[9px] uppercase tracking-wider rounded-sm bg-red-100 text-red-800 border-red-300 font-bold gap-0.5">
                      <Warning size={9} weight="bold" /> $ Changed
                    </Badge>
                  ) : isPending ? (
                    <Badge data-testid={`pending-badge-${exp.id}`}
                      className="text-[9px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-800 border-amber-300 font-bold gap-0.5">
                      <Clock size={9} weight="bold" /> Pending
                    </Badge>
                  ) : exp.mercury_transaction_id ? (
                    <Badge className="text-[9px] uppercase tracking-wider rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">Posted</Badge>
                  ) : (
                    <span className="text-[10px] text-zinc-300">—</span>
                  )}
                </div>
                <div className="col-span-2 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate flex items-center gap-1.5">
                    {exp.is_simulated && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-500 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm flex-shrink-0" data-testid={`exp-sim-badge-${exp.id}`}>
                        TEST
                      </span>
                    )}
                    {exp.vendor_is_new && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-orange-500 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm flex-shrink-0" title="New vendor — verify it isn't a duplicate before approving" data-testid={`exp-new-vendor-badge-${exp.id}`}>
                        NEW VENDOR
                      </span>
                    )}
                    <span className="truncate">{exp.vendor}</span>
                    <ReceiptBadge receiptPath={exp.receipt_path} />
                  </p>
                </div>
                <div className="col-span-2 text-sm text-zinc-600 truncate">{exp.submitted_by_name || "Employee"}</div>
                <div className="col-span-2 min-w-0">
                  <CodingCell exp={exp} />
                </div>
                <div className="col-span-1 text-right min-w-0">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {typeof exp.amount === "number" && exp.amount < 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm flex-shrink-0" title="Refund — pushes to QBO/JT as a credit" data-testid={`exp-refund-amount-badge-${exp.id}`}>
                        REFUND
                      </span>
                    )}
                    <span className={`text-sm font-bold font-mono whitespace-nowrap ${typeof exp.amount === "number" && exp.amount < 0 ? "text-emerald-700" : "text-zinc-900"}`}>{formatCurrency(exp.amount)}</span>
                  </div>
                  {amountChanged && exp.prior_approved_amount != null && (
                    <p className="text-[10px] text-red-700 font-mono mt-0.5" data-testid={`amount-delta-${exp.id}`}>
                      was {formatCurrency(exp.prior_approved_amount)}
                    </p>
                  )}
                </div>
                <div className="col-span-3 flex flex-wrap justify-end items-center gap-1 mt-2 sm:mt-0">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(exp)} data-testid={`view-expense-${exp.id}`}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-700 flex-shrink-0"
                    title="View details">
                    <Eye size={14} />
                  </Button>
                  {!readOnly && (
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/expenses/${exp.id}/edit?from=approvals`)} data-testid={`edit-expense-${exp.id}`}
                      className="h-7 w-7 p-0 text-zinc-500 hover:text-[#002FA7] flex-shrink-0"
                      title="Edit coding or add attachments">
                      <PencilSimple size={14} />
                    </Button>
                  )}
                  {!readOnly && (
                    <Button size="sm" onClick={() => handleApprove(exp.id)} data-testid={`approve-expense-${exp.id}`}
                      title={isPending ? "Approve now — push to QuickBooks/JobTread will fire automatically once the swipe posts (provided the amount doesn't change)." : "Approve + push to QuickBooks/JobTread."}
                      className="h-7 px-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-[11px] gap-1 flex-shrink-0 whitespace-nowrap">
                      <CheckCircle size={11} weight="bold" />
                      {amountChanged ? "Re-approve" : "Approve"}
                    </Button>
                  )}
                  {!readOnly && (
                    <Button size="sm" variant="destructive" onClick={() => handleReject(exp.id)} data-testid={`reject-expense-${exp.id}`}
                      className="h-7 px-2 rounded-sm text-[11px] gap-1 flex-shrink-0 whitespace-nowrap">
                      <XCircle size={11} weight="bold" /> Reject
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckCircle size={40} weight="duotone" className="text-zinc-200 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">All caught up! No expenses pending approval.</p>
          </div>
        )}
      </div>

      {/* Recently Approved + QBO Push Status */}
      {approved.length > 0 && (
        <div className="mt-8" data-testid="recently-approved-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
              Recently Approved &mdash; Sync Status
              {search && visibleApproved.length !== approved.length && (
                <span className="ml-2 text-zinc-500 font-normal normal-case tracking-normal">
                  · {visibleApproved.length} of {approved.length} match "{search}"
                </span>
              )}
            </h2>
            <Button variant="ghost" size="sm" onClick={fetchAll} className="h-7 px-2 text-xs text-zinc-500 gap-1" data-testid="refresh-approved-btn">
              <ArrowsClockwise size={12} /> Refresh
            </Button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
              <div className="col-span-2">Vendor</div>
              <div className="col-span-1">Submitter</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-3">Coding</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-2">Sync Status</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            <div className="divide-y divide-zinc-100">
              {visibleApproved.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-400" data-testid="approved-empty-search">
                  No approved expenses match "{search}".
                </div>
              ) : visibleApproved.map((exp) => (
                <div key={exp.id} className="px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center" data-testid={`approved-row-${exp.id}`}>
                  <div className="col-span-2 min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate flex items-center gap-1.5">
                      <AutopilotBadge exp={exp} />
                      <span className="truncate">{exp.vendor}</span>
                      <ReceiptBadge receiptPath={exp.receipt_path} />
                    </p>
                  </div>
                  <div className="col-span-1 text-sm text-zinc-600 truncate">{exp.submitted_by_name || "Employee"}</div>
                  <div className="col-span-1 text-sm text-zinc-600" data-testid={`approved-dates-${exp.id}`}>
                    <p>{formatDate(exp.transaction_date || exp.date)}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      Posted: {exp.post_date
                        ? <span className="text-zinc-600">{formatDate(exp.post_date)}</span>
                        : !exp.mercury_transaction_id
                          ? <span className="inline-flex items-center px-1 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px] uppercase tracking-wider font-bold" title="Manually entered — no bank transaction" data-testid={`approved-manual-entry-${exp.id}`}>Manual Entry</span>
                          : <span className="text-zinc-300">—</span>}
                    </p>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <CodingCell exp={exp} />
                  </div>
                  <div className="col-span-1 text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {typeof exp.amount === "number" && exp.amount < 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm flex-shrink-0" title="Refund — pushes to QBO/JT as a credit">
                          REFUND
                        </span>
                      )}
                      <span className={`text-sm font-bold font-mono ${typeof exp.amount === "number" && exp.amount < 0 ? "text-emerald-700" : "text-zinc-900"}`}>{formatCurrency(exp.amount)}</span>
                    </div>
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center gap-1">
                    <QboStatusBadge exp={exp} />
                    <JtStatusBadge exp={exp} />
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    {!readOnly && (exp.qbo_push_error || exp.jt_push_error) && !exp.qbo_purchase_id && !exp.jt_document_id && (
                      <Button size="sm" variant="ghost" onClick={() => setErrorDialog(exp)} data-testid={`view-qbo-error-${exp.id}`}
                        className="h-7 px-2 text-zinc-500 hover:text-zinc-700" title="View error details">
                        <Warning size={14} />
                      </Button>
                    )}
                    {!readOnly && (exp.qbo_purchase_id || exp.qbo_push_error || exp.jt_document_id || exp.jt_push_error) && (
                      <Button size="sm" onClick={() => confirmRepush(exp)} disabled={pushing[exp.id]}
                        data-testid={`repush-qbo-${exp.id}`}
                        title={(exp.qbo_purchase_id || exp.jt_document_id)
                          ? "Voids prior records in QBO/JobTread and creates fresh ones."
                          : "Retry the push."}
                        className={`h-7 px-2 rounded-sm text-xs gap-1 disabled:opacity-40 ${
                          (exp.qbo_purchase_id || exp.jt_document_id)
                            ? "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-[#2CA01C]"
                            : "bg-[#2CA01C] hover:bg-[#1f7a14] text-white"
                        }`}>
                        <ArrowsClockwise size={12} weight="bold" className={pushing[exp.id] ? "animate-spin" : ""} />
                        {pushing[exp.id] ? "Pushing…" : "Re-push"}
                      </Button>
                    )}
                    {/* Admin escape hatch: when a JT doc is stuck (id present + error referencing
                        locked/insufficient permission), surface the Force Unlink button. JT locks
                        approved-status docs one-way via API — only a manual JT delete + this button
                        can reset the expense to allow a fresh Re-push. */}
                    {isAdmin(user) && !readOnly && exp.jt_document_id && exp.jt_push_error &&
                      /(locked|insufficient permission|Could not void)/i.test(exp.jt_push_error) && (
                      <Button size="sm" variant="ghost" onClick={() => setUnlinkJtConfirm(exp)}
                        disabled={unlinkingJt[exp.id]}
                        data-testid={`force-unlink-jt-${exp.id}`}
                        title="Admin: clear stored JT document IDs without calling JobTread. Use after manually deleting the locked doc(s) in JT."
                        className="h-7 px-2 text-amber-700 hover:bg-amber-50 hover:text-amber-900 text-xs gap-1">
                        <Warning size={12} weight="bold" />
                        Unlink JT
                      </Button>
                    )}
                    {isAdmin(user) && !readOnly && <MakeAutopilotRuleButton exp={exp} compact />}
                    {!readOnly && !exp.qbo_purchase_id && !exp.qbo_push_error && !exp.jt_document_id && !exp.jt_push_error && (
                      <Button size="sm" variant="ghost" onClick={() => refreshSingleApproved(exp.id)}
                        data-testid={`check-status-${exp.id}`}
                        className="h-7 px-2 text-zinc-400 hover:text-zinc-700 text-xs">
                        Check status
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: "DM Sans, sans-serif" }}>
              Expense Detail
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">Review and approve or reject</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 mt-2">
              {selected.amount_changed_at_post && (
                <div className="px-3 py-2 rounded-sm bg-red-50 border border-red-300 flex items-start gap-2" data-testid="dialog-amount-changed-warning">
                  <Warning size={14} weight="duotone" className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-900 leading-snug">
                    <strong>Posted amount changed.</strong> You approved <span className="font-mono font-bold">{formatCurrency(selected.prior_approved_amount)}</span> but the card swipe posted at <span className="font-mono font-bold">{formatCurrency(selected.post_amount ?? selected.amount)}</span>. Verify the new amount, then re-approve to release the push to QuickBooks &amp; JobTread.
                  </p>
                </div>
              )}
              {selected.vendor_is_new && (
                <div className="px-3 py-2 rounded-sm bg-orange-50 border border-orange-300 flex items-start gap-2" data-testid="dialog-new-vendor-warning">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-orange-500 text-white text-[9px] uppercase tracking-[0.15em] font-bold shadow-sm flex-shrink-0 mt-0.5">NEW VENDOR</span>
                  <p className="text-[11px] text-orange-900 leading-snug">
                    The submitter added <strong>"{selected.vendor}"</strong> as a brand-new vendor. Verify it isn't a duplicate of an existing QBO vendor before approving — once approved, a fresh vendor record will be created in QuickBooks.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Vendor</span>
                  <span className="text-zinc-800 font-medium">{selected.vendor}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Amount</span>
                  <span className="text-zinc-900 font-bold font-mono">{formatCurrency(selected.amount)}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Transaction Date</span>
                  <span className="text-zinc-800">{formatDate(selected.transaction_date || selected.date)}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Post Date</span>
                  <span className="text-zinc-800">{selected.post_date
                    ? formatDate(selected.post_date)
                    : !selected.mercury_transaction_id
                      ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] uppercase tracking-wider font-bold">Manual Entry</span>
                      : <span className="text-zinc-400">— Pending</span>}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Type</span>
                  <span className="text-zinc-800">{selected.expense_type === "job" ? "Job" : "Overhead Account"}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Coding</span>
                  <span className="text-zinc-800">{selected.expense_type === "job" ? selected.job_name : selected.gl_account_name}</span>
                </div>
                {selected.description && (
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Description</span>
                    <span className="text-zinc-800">{selected.description}</span>
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Submitted By</span>
                  <span className="text-zinc-800">{selected.submitted_by_name || "Employee"}</span>
                </div>
                {selected.receipt_path ? (
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1.5">Receipt</span>
                    <ReceiptPreview receiptPath={selected.receipt_path} size="md" />
                  </div>
                ) : (
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block">Receipt</span>
                    <span className="text-xs text-zinc-400 italic">No receipt attached</span>
                  </div>
                )}
              </div>
              {selected.splits?.length > 0 && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold block mb-1">Splits</span>
                  {selected.splits.map((s, i) => (
                    <div key={s._uid || `${selected.id}-split-${i}`} className="text-xs text-zinc-600 bg-zinc-50 p-2 rounded-sm mb-1">
                      {s.type === "job" ? (s.job_name || "Job") : (s.gl_account_name || "Overhead")}: {formatCurrency(s.amount)}
                      {s.budget_item_name && <span className="text-zinc-400 ml-1">/ {s.budget_item_name}</span>}
                    </div>
                  ))}
                </div>
              )}
              <Comments entityType="expense" entityId={selected.id} />
              {!readOnly && (
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => handleApprove(selected.id)} data-testid="dialog-approve-btn"
                    title={(selected.mercury_txn_status || "").toLowerCase() === "pending"
                      ? "Approve now — push to QuickBooks/JobTread will fire automatically once the swipe posts (provided the amount doesn't change)."
                      : ""}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm gap-1">
                    <CheckCircle size={14} weight="bold" />
                    {selected.amount_changed_at_post ? "Re-approve" : "Approve"}
                  </Button>
                  <Button variant="destructive" onClick={() => handleReject(selected.id)} data-testid="dialog-reject-btn"
                    className="flex-1 rounded-sm gap-1">
                    <XCircle size={14} weight="bold" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QBO Re-push Confirmation Dialog */}
      <Dialog open={!!repushConfirm} onOpenChange={() => setRepushConfirm(null)}>        <DialogContent className="rounded-sm max-w-md" data-testid="repush-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "DM Sans, sans-serif" }}>
              <ArrowsClockwise size={18} weight="duotone" className="text-[#2CA01C]" />
              Re-push to QuickBooks?
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {repushConfirm?.vendor} &mdash; {formatCurrency(repushConfirm?.amount)}
            </DialogDescription>
          </DialogHeader>
          {repushConfirm && (
            <div className="space-y-3 mt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 leading-relaxed">
                <p className="font-bold mb-1">No duplicate will be created.</p>
                <p>
                  Expenser will first <span className="font-semibold">void Purchase #{repushConfirm.qbo_purchase_id}</span> in QuickBooks, then push a fresh copy. The voided id is kept on the expense record for audit (<span className="font-mono text-amber-700">qbo_purchase_id_prev</span>).
                </p>
                <p className="mt-2 text-amber-700">
                  Use this if you changed the card mapping, job, GL, or splits, OR if the QBO Purchase was modified/deleted externally and needs to be regenerated.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRepushConfirm(null)} className="rounded-sm text-xs" data-testid="repush-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleRepush(repushConfirm.id)}
                  disabled={pushing[repushConfirm.id]}
                  data-testid="repush-confirm-btn"
                  className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm text-xs gap-1 disabled:opacity-40"
                >
                  <ArrowsClockwise size={12} weight="bold" className={pushing[repushConfirm.id] ? "animate-spin" : ""} />
                  {pushing[repushConfirm.id] ? "Voiding & pushing…" : "Void & re-push"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Force Unlink JT IDs — Admin Escape Hatch */}
      <Dialog open={!!unlinkJtConfirm} onOpenChange={() => setUnlinkJtConfirm(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="force-unlink-jt-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "DM Sans, sans-serif" }}>
              <Warning size={18} weight="duotone" className="text-amber-600" />
              Force-unlink JobTread IDs?
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {unlinkJtConfirm?.vendor} &mdash; {formatCurrency(unlinkJtConfirm?.amount)}
            </DialogDescription>
          </DialogHeader>
          {unlinkJtConfirm && (
            <div className="space-y-3 mt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 leading-relaxed space-y-2">
                <p className="font-bold">Admin escape hatch — use only when JT has locked the doc.</p>
                <p>
                  This clears the stored JT document reference{(unlinkJtConfirm.jt_document_ids?.length || 1) > 1 ? "s" : ""}
                  {" "}
                  <span className="font-mono text-amber-700">
                    {(unlinkJtConfirm.jt_document_ids?.length ? unlinkJtConfirm.jt_document_ids : [unlinkJtConfirm.jt_document_id]).join(", ")}
                  </span>
                  {" "}from this expense WITHOUT calling JobTread.
                </p>
                <p className="font-bold pt-1">Before clicking Unlink, you MUST:</p>
                <ol className="list-decimal list-inside space-y-0.5 pl-1">
                  <li>Open JobTread and manually delete the locked document(s) above.</li>
                  <li>Then click Unlink — this resets the expense.</li>
                  <li>Click Re-push afterwards to create a fresh document cleanly.</li>
                </ol>
                <p className="text-amber-700 pt-1">
                  The cleared IDs are snapshotted to <span className="font-mono">jt_document_ids_prev</span> and an audit comment is added to the expense.
                </p>
              </div>
              <Input
                placeholder="Reason (optional) — e.g. 'deleted locked docs in JT'"
                data-testid="force-unlink-jt-reason"
                id={`force-unlink-reason-${unlinkJtConfirm.id}`}
                className="rounded-sm text-xs"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setUnlinkJtConfirm(null)} className="rounded-sm text-xs" data-testid="force-unlink-jt-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const reasonEl = document.getElementById(`force-unlink-reason-${unlinkJtConfirm.id}`);
                    handleForceUnlinkJt(unlinkJtConfirm.id, reasonEl?.value || "");
                  }}
                  disabled={unlinkingJt[unlinkJtConfirm.id]}
                  data-testid="force-unlink-jt-confirm-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs gap-1 disabled:opacity-40"
                >
                  <Warning size={12} weight="bold" />
                  {unlinkingJt[unlinkJtConfirm.id] ? "Unlinking…" : "Unlink JT IDs"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QBO Push Error Dialog */}
      <Dialog open={!!errorDialog} onOpenChange={() => setErrorDialog(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="qbo-error-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "DM Sans, sans-serif" }}>
              <Warning size={20} weight="duotone" className="text-amber-500" />
              QuickBooks Push Error
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {errorDialog?.vendor} &mdash; {formatCurrency(errorDialog?.amount)}
            </DialogDescription>
          </DialogHeader>
          {errorDialog && (
            <div className="space-y-3 mt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-xs text-amber-900 font-mono whitespace-pre-wrap break-words leading-relaxed">{errorDialog.qbo_push_error}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => copyErr(errorDialog.qbo_push_error)} variant="outline" data-testid="copy-error-btn"
                  className="rounded-sm gap-1 text-xs">
                  <Copy size={12} /> Copy error
                </Button>
                <Button onClick={() => { setErrorDialog(null); confirmRepush(errorDialog); }}
                  data-testid="dialog-repush-btn"
                  className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm gap-1 text-xs flex-1">
                  <ArrowsClockwise size={12} weight="bold" /> Re-push to QBO
                </Button>
              </div>
              <p className="text-[10px] text-zinc-400 leading-relaxed">
                Most failures are caused by missing mappings &mdash; check Settings &rarr; Users (card &rarr; QBO bank account) and Settings &rarr; Jobs (job &rarr; QBO project), then retry.
                <ArrowSquareOut size={9} className="inline ml-0.5" />
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
